<template>
  <div class="ui-textarea-wrapper">
    <textarea
      ref="textarea"
      :class="textareaClasses"
      :value="modelValue"
      :placeholder="placeholder"
      :disabled="disabled"
      :readonly="readonly"
      :required="required"
      :maxlength="maxlength"
      :minlength="minlength"
      :rows="computedRows"
      :cols="cols"
      :autocomplete="autocomplete"
      :autofocus="autofocus"
      v-bind="$attrs"
      @input="onInput"
      @blur="onBlur"
      @focus="onFocus"
      @keydown="onKeydown"
      @scroll="onScroll"
    ></textarea>
  </div>
</template>

<script>
export default {
  name: 'UITextarea',
  inheritAttrs: false,
  emits: ['update:modelValue', 'input', 'blur', 'focus', 'keydown', 'scroll'],
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    maxlength: {
      type: [String, Number],
      default: null
    },
    minlength: {
      type: [String, Number],
      default: null
    },
    rows: {
      type: [String, Number],
      default: 3
    },
    cols: {
      type: [String, Number],
      default: null
    },
    autocomplete: {
      type: String,
      default: null
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    autoResize: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['small', 'normal', 'large'].includes(value)
    },
    variant: {
      type: String,
      default: 'outlined',
      validator: (value) => ['filled', 'outlined'].includes(value)
    },
    invalid: {
      type: Boolean,
      default: false
    },
    fluid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      autoRows: this.rows
    };
  },
  computed: {
    computedRows() {
      return this.autoResize ? this.autoRows : this.rows;
    },
    textareaClasses() {
      const classes = [
        'transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-0 resize-y'
      ];

      // Size classes
      const sizeClasses = {
        small: 'px-3 py-2 text-sm',
        normal: 'px-4 py-2.5 text-base',
        large: 'px-5 py-3 text-lg'
      };
      classes.push(sizeClasses[this.size]);

      // Variant classes
      if (this.variant === 'filled') {
        classes.push('bg-gray-100 border-0 rounded-md');
        if (!this.disabled) {
          classes.push('hover:bg-gray-200 focus:bg-white focus:ring-blue-500');
        }
      } else {
        classes.push('bg-white border rounded-md');
        if (!this.disabled) {
          classes.push('hover:border-gray-400 focus:border-blue-500 focus:ring-blue-500');
        }
      }

      // State classes
      if (this.invalid) {
        if (this.variant === 'filled') {
          classes.push('bg-red-50 focus:bg-red-50 focus:ring-red-500');
        } else {
          classes.push('border-red-500 focus:border-red-500 focus:ring-red-500');
        }
      } else if (this.variant === 'outlined') {
        classes.push('border-gray-300');
      }

      // Disabled state
      if (this.disabled) {
        classes.push('opacity-50 cursor-not-allowed');
        if (this.variant === 'filled') {
          classes.push('bg-gray-100');
        } else {
          classes.push('bg-gray-50 border-gray-300');
        }
      }

      // Readonly state
      if (this.readonly && !this.disabled) {
        classes.push('bg-gray-50');
      }

      // Width
      if (this.fluid) {
        classes.push('w-full');
      }

      // Auto-resize
      if (this.autoResize) {
        classes.push('resize-none');
      }

      return classes.join(' ');
    }
  },
  watch: {
    modelValue: {
      handler() {
        this.$nextTick(() => {
          if (this.autoResize) {
            this.adjustHeight();
          }
        });
      },
      immediate: true
    }
  },
  mounted() {
    if (this.autoResize) {
      this.adjustHeight();
    }
  },
  methods: {
    onInput(event) {
      const value = event.target.value;
      this.$emit('update:modelValue', value);
      this.$emit('input', event);
      
      if (this.autoResize) {
        this.$nextTick(() => {
          this.adjustHeight();
        });
      }
    },
    onBlur(event) {
      this.$emit('blur', event);
    },
    onFocus(event) {
      this.$emit('focus', event);
    },
    onKeydown(event) {
      this.$emit('keydown', event);
    },
    onScroll(event) {
      this.$emit('scroll', event);
    },
    adjustHeight() {
      if (!this.autoResize || !this.$refs.textarea) return;
      
      const textarea = this.$refs.textarea;
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = parseInt(window.getComputedStyle(textarea).lineHeight, 10);
      const minHeight = lineHeight * this.rows;
      
      textarea.style.height = Math.max(scrollHeight, minHeight) + 'px';
    },
    focus() {
      this.$refs.textarea.focus();
    },
    blur() {
      this.$refs.textarea.blur();
    }
  }
};
</script> 